import LoadingButton from '@mui/lab/LoadingButton';
import React from 'react';
import CUActionsAPI from '../../../redux/CUActions/api/CUActionsAPI';
import useShowErrorMessage from '../../../handlingErrors/useShowErrorMessage';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useCurrentStateByControlUnitId } from '../../../redux/controlUnit/hooks/useCurrentStateByControlUnitId';
import { getPolyglot } from '../../../i18n';
import IconSwitch from './SwitchButton';

interface StatusProps {
  value: boolean | undefined;
  icon: React.ReactElement;
  name?: string;
  deviceId: string;
  controlUnitId: string;
  actionId: string;
  CUTranslate: ReturnType<typeof useCurrentStateByControlUnitId>['CUTranslate'];
  isDeviceConnected?: boolean;
  isDeviceOn?: boolean;
}
function Status(props: StatusProps) {
  const polyglot = getPolyglot();
  const {
    value,
    name,
    icon,
    actionId,
    controlUnitId,
    deviceId,
    CUTranslate,
    isDeviceConnected,
    isDeviceOn,
  } = props;

  const showErrorMessage = useShowErrorMessage();
  const [triggerAction, { isLoading }] = CUActionsAPI.useTriggerActionMutation({
    fixedCacheKey: `triggerAction-${deviceId}-${controlUnitId}-${actionId}`,
  });

  const internalTranslation = polyglot.t(`device.actions.${actionId}`, {
    _: actionId,
  });
  const translation = (CUTranslate(actionId) as string) || internalTranslation;
  const actionName = name || translation;
  const disableForSecurityReasons = true;

  const onClick = () => {
    if (disableForSecurityReasons) {
      showErrorMessage('device.controlUnit.action.error.security', {
        variant: 'warning',
      });
      return;
    } else {
      triggerAction({
        deviceId,
        controlUnitId: parseInt(controlUnitId),
        actionId,
        value: !value,
      })
        .unwrap()
        .catch((error) => {
          error.message.startsWith('POLLING_')
            ? showErrorMessage('device.controlUnit.action.error.pooling', {
                variant: 'warning',
              })
            : showErrorMessage(error);
        });
    }
  };
  return (
    <Stack spacing={0.5} direction={'column'} alignItems={'center'}>
      <Box
        sx={{
          flexDirection: 'column',
          opacity: isDeviceOn ? 1 : !isDeviceConnected ? 0.5 : 0.7,
        }}
      >
        {!isLoading ? (
          <>
            <Typography
              textAlign="center"
              fontSize={'1rem'}
              color={
                !isDeviceConnected
                  ? 'text.disabled'
                  : isDeviceOn
                  ? 'text.primary'
                  : 'text.secondary'
              }
              sx={{ opacity: isDeviceConnected ? 1 : 0.5 }}
            >
              {actionName}
            </Typography>
            <Box
              sx={{
                fontSize: '1.5em',
                display: 'flex',
                color:
                  value && isDeviceConnected
                    ? (theme) => theme.palette.primary.main
                    : (theme) => theme.palette.text.disabled,
                justifyContent: 'center',
              }}
              padding={0.2}
            >
              {isDeviceConnected ? (
                <IconSwitch
                  checked={value}
                  iconOn={icon}
                  iconOff={icon}
                  onClick={onClick}
                  disabled={!isDeviceConnected}
                />
              ) : (
                icon
              )}
            </Box>
          </>
        ) : (
          <Box>
            <CircularProgress />
          </Box>
        )}
      </Box>
    </Stack>
  );
}

export default Status;
